/**
 * MCP (Model Context Protocol) servers — user-configured external tool providers that the on-device AI
 * Workflows agent can load tools from. Surfaced to users as "Tools". A server declares a transport
 * (stdio/CLI, streamable HTTP, or SSE) and, optionally, authentication (API key, custom headers, or
 * OAuth 2.0). When a server is selected for a run (auto by relevance, or manually via `/`·`@`), the
 * worker connects with `@langchain/mcp-adapters` and injects that server's tools into the agent.
 *
 * These models live in `shared/` so both the desktop renderer (Extensions "Tools" manager, composer
 * picker) and the desktop worker/agent can use them. This is the redacted, renderer-safe shape:
 * secret values (API keys, OAuth tokens/secrets, secret headers/env vars) NEVER appear here — only
 * boolean "is set" flags. Secrets are held encrypted main-process-side by McpService and injected into
 * a {@link ResolvedMcpConnection} for the worker at run time.
 *
 * Mirrors the AI Skills model shape (see shared/models/skills/SkillModel.ts) so the two Extensions
 * features stay parallel.
 */

/** How a server talks to Cephable. Matches the transports `@langchain/mcp-adapters` supports. */
export type McpTransport = 'stdio' | 'http' | 'sse';

/** Where a server config came from (mirrors {@link import('@shared/models/skills/SkillModel').SkillSource}). */
export type McpSource = 'user' | 'imported' | 'organization' | 'marketplace';

/**
 * How a server authenticates:
 * - `none` — no auth (local stdio servers, open HTTP servers).
 * - `apiKey` — a single secret sent as a header (default `Authorization: Bearer <key>`; header name
 *   and value prefix are configurable). HTTP/SSE only.
 * - `headers` — arbitrary custom headers; any subset of their values may be stored as secrets. HTTP/SSE only.
 * - `oauth` — OAuth 2.0 authorization-code + PKCE. The interactive flow runs in the main process; the
 *   worker receives only a fresh bearer access token. HTTP/SSE only.
 * For `stdio`, secrets are supplied as (optionally secret) environment variables instead — `authType`
 * is `none` and `secretEnvKeys` marks which env values are stored encrypted.
 */
export type McpAuthType = 'none' | 'apiKey' | 'headers' | 'oauth';

/**
 * Which redirect (callback) URI an OAuth server is asked to send the authorization code back to. Some
 * providers won't register an `https://` callback on a public domain without an approval/waitlist step,
 * so the same flow has to be reachable three ways:
 * - `appCallback` — the Cephable-hosted `/app/open` endpoint, which bounces into the app's deep link.
 * - `localhost` — RFC 8252 loopback: the main process listens on the loopback callback for the length
 *   of the flow.
 */
export type McpOauthRedirectType = 'appCallback' | 'localhost';

/**
 * How an enabled server is chosen for a run (mirrors {@link SkillActivation}):
 * - `auto` — the assistant may load it automatically when the prompt matches, AND the user can attach
 *   it explicitly via `/`·`@`.
 * - `manual` — never loaded automatically; only when the user attaches it via `/`·`@` or the context adder.
 * Only meaningful when the server is `enabled`.
 */
export type McpActivation = 'auto' | 'manual';

/**
 * What a server starts as however it was added — configured by hand or installed from an org/marketplace
 * (mirrors {@link DEFAULT_SKILL_ACTIVATION}). Manual for now: loading a server pulls every one of its tool
 * schemas into a small on-device model's window, and we need better token/memory management before dynamic
 * runtime selection can be trusted. Users opt servers into `auto`.
 */
export const DEFAULT_MCP_ACTIVATION: McpActivation = 'manual';

/** Liveness of the last connection attempt, shown as a badge in the editor/section. */
export type McpConnectionStatus = 'unknown' | 'ok' | 'error';

export const MAX_MCP_NAME_LENGTH = 64;
export const MAX_MCP_DESCRIPTION_LENGTH = 1024;

/** One tool advertised by a server, cached after a successful connect for auto-relevance + picker display. */
export interface McpToolSummary {
    name: string;
    description: string;
    /**
     * Estimated token cost of this tool's full definition (name + description + JSON schema) when loaded
     * into the agent's context, computed at connect time (~chars/4 heuristic). Undefined for tools cached
     * before estimation existed. Used to warn about context/token bloat and drive per-tool selection.
     */
    tokenEstimate?: number;
}

/**
 * A single configured MCP server, as surfaced to the renderer and threaded (redacted) to the worker.
 * Secret values are excluded — only `has*` / `*Keys` flags indicate what is stored.
 */
export interface McpServerModel {
    /** Stable id (equals the slugified name in v1; kept distinct so a future rename can preserve it). */
    id: string;
    /** Display + config key: lowercase alphanumeric + hyphens, 1–64 chars (see mcpValidation). */
    name: string;
    /** What the server does AND when to use it. Drives auto-relevance selection. */
    description: string;
    transport: McpTransport;

    // --- stdio transport ---
    /** Executable to spawn (e.g. `npx`). stdio only. */
    command?: string;
    /** Arguments for the executable. stdio only. */
    args?: string[];
    /** Non-secret environment variables. stdio only. */
    env?: Record<string, string>;
    /** Names of env vars whose values are stored encrypted (values not included here). stdio only. */
    secretEnvKeys?: string[];

    // --- http / sse transport ---
    /** Server endpoint URL. http/sse only. */
    url?: string;
    /** Non-secret custom headers. http/sse only. */
    headers?: Record<string, string>;
    /** Names of headers whose values are stored encrypted (values not included here). http/sse only. */
    secretHeaderKeys?: string[];

    // --- auth ---
    authType: McpAuthType;
    /** apiKey: header to place the key in (default `Authorization`). */
    apiKeyHeaderName?: string;
    /** apiKey: value prefix (default `Bearer `). */
    apiKeyPrefix?: string;
    /** True when an API key secret is stored. */
    hasApiKey?: boolean;
    /** oauth: registered/known client id (may be absent when using dynamic client registration). */
    oauthClientId?: string;
    /** oauth: requested scopes (space-joined preserved as array). */
    oauthScopes?: string[];
    /** oauth: RFC 8414 metadata / issuer URL to discover endpoints from. */
    oauthDiscoveryUrl?: string;
    /** oauth: explicit authorization endpoint (used when discovery is unavailable). */
    oauthAuthorizationUrl?: string;
    /** oauth: explicit token endpoint (used when discovery is unavailable). */
    oauthTokenUrl?: string;
    /** oauth: which callback the provider redirects to (see {@link McpOauthRedirectType}). Default `appCallback`. */
    oauthRedirectType?: McpOauthRedirectType;
    /** oauth: loopback port to listen on when `oauthRedirectType` is `localhost`. Default {@link DEFAULT_MCP_OAUTH_LOCALHOST_PORT}. */
    oauthLocalhostPort?: number;
    /** True when an OAuth client secret is stored (confidential clients). */
    hasOauthClientSecret?: boolean;
    /** True when a valid OAuth token set is stored (i.e. the user has completed the browser flow). */
    oauthConnected?: boolean;

    /** When false, hidden from selection and the picker (configured but paused). */
    enabled: boolean;
    /** Auto vs manual (see {@link McpActivation}). Only meaningful when `enabled`. */
    activation: McpActivation;
    /**
     * When true, every tool call from this server pauses for the user's approve/edit/reject decision
     * (reuses the agent's native destructive-tool interrupt). Defaults to true — MCP tools can perform
     * arbitrary external side effects.
     */
    requireApproval: boolean;

    /** Tools discovered on the last successful connect (name + description). Empty until tested/refreshed. */
    cachedTools: McpToolSummary[];
    /**
     * Bare tool names (no `mcp__<serverKey>__` prefix) the user has turned OFF for this server, so they
     * are excluded from the agent's context to control token/context bloat. Absent/empty ⇒ all tools
     * enabled. Pruned to the current `cachedTools` set on each successful connect.
     */
    disabledTools?: string[];
    connectionStatus: McpConnectionStatus;
    /** Human-readable reason for the last `error` status. */
    lastError?: string;
    /** Epoch ms of the last successful connect. */
    lastConnectedAt?: number;

    createdAt: number;
    updatedAt: number;

    // --- server sync (present on servers published to / installed from the API) ---
    /** Server id of this server's API registration (the `McpServerRegistrationModel.id`). */
    remoteId?: string;
    /** Server `modifiedDate` epoch-ms captured at the last publish/install; a newer server value = update available. */
    remoteVersion?: number;
    /** Organization this server is scoped to on the server, if any. */
    organizationId?: string;
    /** True when this is official Cephable-managed content. */
    isOfficial?: boolean;
    /** Visibility of the server-side registration (`organization` / `public`), when shared. */
    remoteVisibility?: 'organization' | 'public';
    /** Where this config came from: locally authored (`user`) vs installed from org/marketplace. */
    source?: McpSource;
}

/**
 * Server-sync metadata stamped onto an MCP server when it is published to / installed from the API.
 * Reconciles the local (non-secret) config with its server registration; secrets stay on-device.
 */
export interface RemoteMcpMeta {
    remoteId: string;
    remoteVersion?: number;
    organizationId?: string;
    isOfficial?: boolean;
    remoteVisibility?: 'organization' | 'public';
}

/** Payload for adding a server. Secrets are set via separate setSecret calls, never here. */
export interface CreateMcpServerInput {
    name: string;
    description: string;
    transport: McpTransport;
    command?: string;
    args?: string[];
    env?: Record<string, string>;
    url?: string;
    headers?: Record<string, string>;
    authType?: McpAuthType;
    apiKeyHeaderName?: string;
    apiKeyPrefix?: string;
    oauthClientId?: string;
    oauthScopes?: string[];
    oauthDiscoveryUrl?: string;
    oauthAuthorizationUrl?: string;
    oauthTokenUrl?: string;
    oauthRedirectType?: McpOauthRedirectType;
    oauthLocalhostPort?: number;
    activation?: McpActivation;
    requireApproval?: boolean;
    disabledTools?: string[];
}

/** Fields the editor can change on an existing server. Secrets are set via separate setSecret calls. */
export interface UpdateMcpServerInput {
    id: string;
    name?: string;
    description?: string;
    transport?: McpTransport;
    command?: string;
    args?: string[];
    env?: Record<string, string>;
    secretEnvKeys?: string[];
    url?: string;
    headers?: Record<string, string>;
    secretHeaderKeys?: string[];
    authType?: McpAuthType;
    apiKeyHeaderName?: string;
    apiKeyPrefix?: string;
    oauthClientId?: string;
    oauthScopes?: string[];
    oauthDiscoveryUrl?: string;
    oauthAuthorizationUrl?: string;
    oauthTokenUrl?: string;
    oauthRedirectType?: McpOauthRedirectType;
    oauthLocalhostPort?: number;
    enabled?: boolean;
    activation?: McpActivation;
    requireApproval?: boolean;
    /** Bare tool names to exclude from this server (see {@link McpServerModel.disabledTools}). */
    disabledTools?: string[];
}

/** Result of validating a server field, for inline form feedback. */
export interface McpValidationResult {
    valid: boolean;
    /** Human-readable reason when invalid (surfaced under the field). */
    error?: string;
}

/** Kinds of secret a server can store. Used by the main-process secret store + the redaction flags above. */
export type McpSecretKind = 'apiKey' | 'oauthClientSecret' | 'oauthTokens' | 'header' | 'env';

/**
 * The de-redacted connection details the worker needs to actually connect to ONE server, produced by
 * McpService.resolveConfig (main process) with all secrets injected and any OAuth access token
 * refreshed. This is main→worker only and must NEVER be sent to the renderer. `serverKey` is the key
 * used in the MultiServerMCPClient `mcpServers` map (the server's slug); MCP tool names are prefixed
 * with it (see MCP_TOOL_NAME_PREFIX) so they can't collide with built-in tools.
 */
export interface ResolvedMcpConnection {
    serverId: string;
    serverKey: string;
    serverName: string;
    transport: McpTransport;
    requireApproval: boolean;
    /** Bare tool names to exclude from this server's loaded tools (see {@link McpServerModel.disabledTools}). */
    disabledTools?: string[];
    // stdio
    command?: string;
    args?: string[];
    env?: Record<string, string>;
    // http/sse
    url?: string;
    headers?: Record<string, string>;
}

/**
 * Prefix applied to every MCP tool name (`mcp__<serverKey>__<tool>`) via mcp-adapters'
 * prefixToolNameWithServerName + additionalToolNamePrefix. Lets the agent/middleware recognize MCP
 * tools by name and avoids collisions with Cephable's built-in tools.
 */
export const MCP_TOOL_NAME_PREFIX = 'mcp';

/** True when a tool name was produced by an MCP server (see {@link MCP_TOOL_NAME_PREFIX}). */
export function isMcpToolName(name: string): boolean {
    return name.startsWith(`${MCP_TOOL_NAME_PREFIX}__`);
}
